/**
 * /validation/runs/:runId — one release's journey, stage by stage.
 *
 * The page is a stepper (where the run is) over tabs (the work of each stage).
 * Every human gate is a button on the tab where the decision is made:
 *
 *   Risk   → "Confirm risks"            (gate 1 — also drafts the plan)
 *   Tests  → "Approve tests & assign"   (gate 2 — opens execution)
 *   Sign   → "Re-authenticate and sign" (gate 3 — e-signature)
 *
 * Everything the AI drafts is shown with its provenance (prompt version and
 * the `ai_inference` id) and is editable by a human before the next stage
 * consumes it.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Stepper,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChecklist,
  IconClipboardText,
  IconFileReport,
  IconPlayerPlay,
  IconShieldHalf,
  IconSignature,
  IconTrash,
} from "@tabler/icons-react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { deleteRun, getRun, type RunOut } from "@/shared/api/validation";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { ApprovalStage } from "./stages/ApprovalStage";
import { ExecutionStage } from "./stages/ExecutionStage";
import { PlanStage } from "./stages/PlanStage";
import { ReportStage } from "./stages/ReportStage";
import { RiskStage } from "./stages/RiskStage";
import { TestsStage } from "./stages/TestsStage";
import {
  READINESS_COLOR,
  READINESS_LABEL,
  STAGES,
  STATUS_COLOR,
  STATUS_LABEL,
  activeTab,
  fmtDate,
  stageIndex,
} from "./stageMeta";

export function RunDetail() {
  const { runId = "" } = useParams();
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [run, setRun] = useState<RunOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const fresh = await getRun(token, runId);
      setRun(fresh);
      setTab((prev) => prev ?? activeTab(fresh.status));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every stage action returns the refreshed run; jump to the stage that
   *  now needs attention so the lead never hunts for the next button. */
  const onUpdated = (fresh: RunOut, jump = true) => {
    setRun(fresh);
    if (jump) setTab(activeTab(fresh.status));
  };

  const onDelete = async () => {
    if (!run) return;
    const reason = await promptForReason("Delete validation run", "Audit reason", {
      confirmLabel: "Delete",
      warning:
        "The run, its tests, and its evidence are removed. The audit trail keeps the record.",
    });
    if (!reason) return;
    try {
      await deleteRun(await getToken(), run.run_id, reason);
      toastSuccess("Run deleted", run.title);
      navigate("/validation/runs");
    } catch (err) {
      toastApiError("Could not delete", err);
    }
  };

  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load the run">
        {error}
      </Alert>
    );
  }
  if (!run) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  const idx = stageIndex(run.status);
  const rejected = run.status === "rejected";

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Button
          component={RouterLink}
          to="/validation/runs"
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
        >
          Runs
        </Button>
      </Group>

      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <Title order={2}>{run.title}</Title>
            <Badge variant="light" color={STATUS_COLOR[run.status]}>
              {STATUS_LABEL[run.status]}
            </Badge>
            {run.readiness && (
              <Badge
                variant="filled"
                color={READINESS_COLOR[run.readiness]}
                leftSection={run.status === "approved" ? <IconSignature size={12} /> : undefined}
              >
                {READINESS_LABEL[run.readiness]}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {run.vault_product}
            {run.change_ref ? ` · ${run.change_ref}` : ""} · owner{" "}
            {run.owner_display_name ?? run.owner_email ?? "—"} · created{" "}
            {fmtDate(run.created_at_utc)} · model {run.deployment}
          </Text>
        </div>
        {run.is_owner && run.status !== "approved" && (
          <Tooltip label="Delete run (reason required)">
            <ActionIcon variant="subtle" color="red" onClick={() => void onDelete()}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      {run.last_error && (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertTriangle size={18} />}
          title="The last AI stage did not complete"
        >
          <Text size="sm">{run.last_error}</Text>
          <Text size="xs" c="dimmed" mt={4}>
            The attempt is recorded. Retry from the stage's tab.
          </Text>
        </Alert>
      )}

      {rejected && (
        <Alert variant="light" color="red" icon={<IconAlertTriangle size={18} />} title="Rejected">
          <Text size="sm">{run.rejection_reason}</Text>
          <Text size="xs" c="dimmed" mt={4}>
            Reopen from the Sign-off tab to re-test; passed tests keep their record.
          </Text>
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stepper
          active={rejected ? STAGES.length - 1 : idx}
          size="xs"
          iconSize={26}
          color={rejected ? "red" : run.status === "approved" ? "green" : "brand"}
          onStepClick={(i) => setTab(STAGES[i]?.tab ?? "risk")}
          allowNextStepsSelect
        >
          {STAGES.map((s) => (
            <Stepper.Step key={s.key} label={s.label} description={s.hint} />
          ))}
        </Stepper>
      </Paper>

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="risk" leftSection={<IconShieldHalf size={14} />}>
            Risk assessment
            <CountChip n={run.risks?.filter((r) => r.confirmed).length} />
          </Tabs.Tab>
          <Tabs.Tab value="plan" leftSection={<IconClipboardText size={14} />}>
            Validation plan
            {run.plan_version > 0 && <CountChip label={`v${run.plan_version}`} />}
          </Tabs.Tab>
          <Tabs.Tab value="tests" leftSection={<IconChecklist size={14} />}>
            Tests
            <CountChip n={run.tests.length} />
          </Tabs.Tab>
          <Tabs.Tab value="execution" leftSection={<IconPlayerPlay size={14} />}>
            Execution
            <CountChip
              n={run.tests.filter((t) => ["passed", "failed", "blocked"].includes(t.status)).length}
              of={run.tests.length}
            />
          </Tabs.Tab>
          <Tabs.Tab value="report" leftSection={<IconFileReport size={14} />}>
            Report
          </Tabs.Tab>
          <Tabs.Tab value="approval" leftSection={<IconSignature size={14} />}>
            Sign-off
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="risk" pt="md">
          <RiskStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
        <Tabs.Panel value="plan" pt="md">
          <PlanStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
        <Tabs.Panel value="tests" pt="md">
          <TestsStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
        <Tabs.Panel value="execution" pt="md">
          <ExecutionStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
        <Tabs.Panel value="report" pt="md">
          <ReportStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
        <Tabs.Panel value="approval" pt="md">
          <ApprovalStage run={run} onUpdated={onUpdated} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function CountChip({ n, of, label }: { n?: number; of?: number; label?: string }) {
  const text = label ?? (n === undefined ? null : of !== undefined ? `${n}/${of}` : String(n));
  if (!text) return null;
  return (
    <Badge size="xs" variant="light" color="gray" ml={6}>
      {text}
    </Badge>
  );
}
